import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import pluralize from 'pluralize'
import toast from 'react-hot-toast'

import type {
  AccountDocumentCenterDTO,
  AssociateDocLoadRequest,
  BulkDocEntryReviewRequest,
  BulkDocEntryReviewResponse,
} from '@fv/models'

import { shipperFetch } from '@/utils/shipperFetch'

import type { DocCenterListItem } from './types'

const queryKeyRoot = ['doc-center']
export const queryKeys = {
  docs: [...queryKeyRoot, 'docs'],
  info: [...queryKeyRoot, 'info'],
}

const fetchDocCenterListing = async (): Promise<DocCenterListItem[]> =>
  await shipperFetch<DocCenterListItem[]>('/doc-center/docs')

export const useDocCenterListing = () =>
  useQuery({
    queryKey: queryKeys.docs,
    queryFn: fetchDocCenterListing,
  })

const fetchDocumentCenterInfo = async (): Promise<AccountDocumentCenterDTO> =>
  shipperFetch<AccountDocumentCenterDTO>('/doc-center')

export const useDocumentCenterInfo = () =>
  useQuery({
    queryKey: queryKeys.info,
    queryFn: fetchDocumentCenterInfo,
  })

const createDocumentCenterInbox = async (): Promise<AccountDocumentCenterDTO> =>
  shipperFetch('/doc-center/setup', {
    method: 'POST',
  })

export const useCreateDocumentCenterInbox = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: createDocumentCenterInbox,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.info })
    },
  })
}

const bulkActionDocuments = async (
  body: BulkDocEntryReviewRequest,
): Promise<BulkDocEntryReviewResponse> =>
  shipperFetch('/doc-center/docs/actions', {
    method: 'POST',
    body,
  })

export const useBulkReview = () => {
  const invalidator = useInvalidateDocCache()

  return useMutation({
    mutationFn: bulkActionDocuments,
    onSuccess: (results, variables) => {
      const successCount = results.filter(r => r.success).length
      const errorCount = results.filter(r => !r.success).length

      if (successCount > 0) {
        toast.success(
          `Successfully ${variables.action === 'approve' ? 'approved' : 'rejected'} ${successCount} ${pluralize('document', successCount)}`,
        )
      }

      if (errorCount > 0) {
        toast.error(
          `Failed to ${variables.action === 'approve' ? 'approve' : 'reject'} ${errorCount} ${pluralize('document', errorCount)}`,
        )
      }

      // Refresh the documents list
      invalidator()
    },
    onError: (error, variables) => {
      toast.error(`Failed to ${variables.action} documents: ${error.message}`)
    },
  })
}

const putDocAssociation = (docId: string) => (body: AssociateDocLoadRequest) =>
  shipperFetch(`/doc-center/docs/${docId}`, {
    method: 'PUT',
    body,
  })

export const useAssociateDocLoad = (docId: string) => {
  const invalidateDocCache = useInvalidateDocCache()
  return useMutation({
    mutationFn: putDocAssociation(docId),
    onSuccess: () => {
      invalidateDocCache()
    },
    onError: error => {
      toast.error(`Failed to associate document: ${error.message}`)
    },
  })
}

export const useInvalidateDocCache = () => {
  const queryClient = useQueryClient()
  return () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.docs })
  }
}

const archiveDocument = async (docId: string) =>
  shipperFetch(`/doc-center/docs/${docId}`, {
    method: 'DELETE',
  })

export const useArchiveDocument = () => {
  const invalidator = useInvalidateDocCache()
  return useMutation({
    mutationFn: archiveDocument,
    onSuccess: () => {
      invalidator()
    },
  })
}
